import { supportArticles } from "lightning-common/lib/configuration";
import { SupportArticles } from "lightning-common/lib/types";
import { PartnerLibrary } from "lightning-common/lib/types/lightning-api";
import { LibraryConfiguration } from "lightning-common/lib/types/lightning-config";
import { LibraryPage, LibraryPages } from "lightning-common/lib/types/lightning-config/page";
import { FormatId, Language, LendingPeriods, Link, MediaType, Message } from "lightning-common/lib/types/thunder";
import { selector, useRecoilValue } from "recoil";
import { getLoadedLibraryData } from "./raw-library-data";

interface LibraryLinks {
    libraryHome?: Link;
    librarySupportEmail?: Link;
    librarySupportUrl?: Link;
}

interface SocialLinks {
    facebook?: Link;
    pinterest?: Link;
    twitter?: Link;
    youtube?: Link;
}

type Library = Partial<LibraryConfiguration> & {
    androidFooterUrl: string;
    androidUrl: string;
    customFooter?: {
        html?: string | { [key: string]: string };
    };
    customHtml?: string;
    iOSFooterUrl: string;
    iOSUrl: string;
    usesLibby: boolean;
    winUrl: string;
};

interface LibraryInfo {
    accountSignInUrl: string;
    advantageKey?: string;
    cardSignUpLink: string | null;
    currentPage?: LibraryPage;
    currentRoom?: LibraryPage;
    digitalCollectionType: string;
    enabledPlatforms: string[];
    gacEnabled: boolean;
    hasKanopySubscription: boolean;
    isConsortium: boolean;
    isDLR: boolean;
    isLocal?: boolean;
    kanopyImageUrls: string[];
    kanopySignUpUrl?: string;
    languages: Language[];
    library: Library;
    libraryId: number;
    libraryKey: string;
    libraryLinks: LibraryLinks;
    libraryName: string;
    logo?: string;
    luckyDayLendingPeriodsByFormat: LendingPeriods;
    messages?: Message[];
    pagesData: LibraryPages;
    parentAccountKey: string | undefined;
    partnerLibraries: PartnerLibrary[];
    roomKey?: string;
    showAdvantageTout: boolean;
    showHistoryFeature: boolean;
    socialLinks: SocialLinks;
    socialLinksLength: number;
    supportArticles: SupportArticles;
    supportedFacets: string[];
    supportedFormats: FormatId[];
    systemMessaging: object;
    tenant: number;
    whitelistedMediaTypes: MediaType[];
}

const DEFAULT_LIBRARY_INFO: LibraryInfo = {
    accountSignInUrl: '',
    cardSignUpLink: null,
    digitalCollectionType: '',
    enabledPlatforms: [],
    hasKanopySubscription: false,
    kanopySignUpUrl: '',
    isConsortium: false,
    isDLR: false,
    isLocal: false,
    gacEnabled: false,
    kanopyImageUrls: [],
    languages: [],
    library: {
        androidFooterUrl: '',
        androidUrl: '',
        iOSFooterUrl: '',
        iOSUrl: '',
        usesLibby: false,
        winUrl: ''
    },
    libraryId: 0,
    libraryKey: '',
    libraryLinks: {},
    libraryName: '',
    luckyDayLendingPeriodsByFormat: {},
    pagesData: {
        items: []
    },
    parentAccountKey: undefined,
    partnerLibraries: [],
    showAdvantageTout: false,
    showHistoryFeature: false,
    socialLinks: {},
    socialLinksLength: 0,
    supportArticles,
    supportedFacets: [],
    supportedFormats: [],
    systemMessaging: {},
    tenant: 0,
    whitelistedMediaTypes: []
};

export const libraryInfoState = selector<LibraryInfo>({
    key: "LibraryInfo",
    get: ({ get }) => {
        const data = getLoadedLibraryData(get);
        if (!data) {
            return DEFAULT_LIBRARY_INFO;
        }

        return {
            accountSignInUrl: data.accountSignInUrl,
            advantageKey: data.advantageKey,
            cardSignUpLink: data.cardSignUpLink,
            currentPage: data.currentPage,
            currentRoom: data.currentRoom,
            digitalCollectionType: data.digitalCollectionType,
            enabledPlatforms: data.enabledPlatforms,
            gacEnabled: data.gacEnabled,
            hasKanopySubscription: data.hasKanopySubscription,
            kanopySignUpUrl: data.kanopySignUpUrl,
            isConsortium: data.isConsortium,
            isDLR: data.isDLR,
            isLocal: data.isLocal,
            kanopyImageUrls: data.kanopyImageUrls ?? [],
            languages: data.languageList,
            library: data.library,
            libraryId: data.libraryId,
            libraryKey: data.libraryKey,
            libraryLinks: data.libraryLinks,
            libraryName: data.libraryName,
            logo: data.logo,
            luckyDayLendingPeriodsByFormat: data.LuckyDayLendingPeriodsByFormat,
            messages: data.messages,
            pagesData: data.pagesData,
            parentAccountKey: data.parentAccountKey,
            partnerLibraries: data.partnerLibraries ?? [],
            roomKey: data.roomKey,
            showAdvantageTout: data.showAdvantageTout,
            showHistoryFeature: data.showHistoryFeature,
            socialLinks: data.socialLinks,
            socialLinksLength: data.socialLinksLength,
            supportArticles: data.supportArticles,
            supportedFacets: data.supportedFacets,
            supportedFormats: data.supportedFormats,
            systemMessaging: data.systemMessaging,
            tenant: data.tenant,
            whitelistedMediaTypes: data.whitelistedMediaTypes
        } as LibraryInfo;
    }
});

export const useLibraryInfo = () => useRecoilValue(libraryInfoState);
